// จอมอนิเตอร์สุขภาพระบบ — ทะเบียนจอ ESP32 ที่วางบนโต๊ะผู้ดูแล
//
// หน้านี้มีสองส่วน: ตัวอย่างสิ่งที่จอกำลังแสดงอยู่จริง (ดึงตัวเลขชุดเดียวกับที่จอดึง)
// กับทะเบียนจอ ตัวอย่างอยู่บนสุดเพราะเป็นสิ่งที่ผู้ดูแลเปิดมาดูบ่อยที่สุด ส่วนทะเบียน
// แตะปีละครั้งตอนเพิ่มจอหรือออก token ใหม่
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';
import { useLoading } from '../contexts/LoadingContext';

const API = process.env.REACT_APP_API;

// จอเคาะทุก 30 วิ ให้หน้าเว็บดึงจังหวะเดียวกัน — ตัวอย่างจะได้ตรงกับของจริง
const POLL_MS = 30 * 1000;

// เกินสามรอบ poll แล้วยังไม่มีข้อมูลใหม่ = ถือว่าจอนั้นเงียบไป
const ONLINE_MS = 3 * POLL_MS;

// สีของจอเป็นคีย์จาก server (utils/healthLevel.js) ไม่ใช่ข้อความ — หน้าเว็บกับ
// เฟิร์มแวร์แปลเป็นภาษาคนละที่กัน แต่ตัดสินใจจากคีย์ชุดเดียวกัน
const LEVEL = {
  green: { variant: 'success', text: 'ปกติ', icon: 'ri-checkbox-circle-fill' },
  amber: { variant: 'warning', text: 'น่าสงสัย', icon: 'ri-error-warning-fill' },
  red: { variant: 'danger', text: 'ผิดปกติ', icon: 'ri-alarm-warning-fill' },
};

// เหตุผลที่ทำให้สีเปลี่ยน — ฝั่งจอขึ้นเป็นไอคอน ส่วนที่นี่เขียนเต็มได้
const REASON_TEXT = {
  db: 'ต่อฐานข้อมูลไม่ได้',
  errors: 'มี 5xx ในชั่วโมงที่ผ่านมา',
  slow: 'เซิร์ฟเวอร์ตอบสนองช้า',
  logins: 'เข้าระบบไม่สำเร็จถี่ผิดปกติ',
  quiet: 'ไม่มีการใช้งานเลยทั้งที่อยู่ในเวลาราชการ',
};

const thaiDateTime = (v) => (v
  ? new Date(v).toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—');

const sinceText = (v) => {
  if (!v) return 'ยังไม่เคยเห็น';
  const secs = Math.round((Date.now() - new Date(v).getTime()) / 1000);
  if (secs < 90) return 'เมื่อครู่';
  if (secs < 3600) return `${Math.round(secs / 60)} นาทีที่แล้ว`;
  if (secs < 86400) return `${Math.round(secs / 3600)} ชม.ที่แล้ว`;
  return thaiDateTime(v);
};

const uptimeText = (secs) => {
  if (!Number.isFinite(secs)) return '—';
  if (secs < 3600) return `${Math.round(secs / 60)} นาที`;
  if (secs < 86400) return `${(secs / 3600).toFixed(1)} ชม.`;
  return `${Math.round(secs / 86400)} วัน`;
};

const isOnline = (device) => !!device.last_seen_at
  && Date.now() - new Date(device.last_seen_at).getTime() < ONLINE_MS;

// คัดลอกไม่สำเร็จต้องไม่เงียบ — แอดมินจะกดแล้วไปวางในโปรแกรมแฟลชโดยไม่รู้ว่าคลิปบอร์ดว่าง
const copy = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    Swal.fire({ icon: 'success', title: `คัดลอก${what}แล้ว`, timer: 1200, showConfirmButton: false });
  } catch {
    Swal.fire({ icon: 'warning', title: 'คัดลอกอัตโนมัติไม่ได้', text: 'กรุณาเลือกข้อความแล้วคัดลอกเอง' });
  }
};

const emptyDevice = { device_id: null, device_code: '', device_name: '', note: '', is_active: true };

export default function AdminMonitorDevices() {
  const { setLoading } = useLoading();
  const [devices, setDevices] = useState([]);
  const [beacon, setBeacon] = useState(null);
  const [beaconFailed, setBeaconFailed] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [deviceForm, setDeviceForm] = useState(null);   // null = ปิดฟอร์ม
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // ให้ "อัปเดตเมื่อ 40 วิที่แล้ว" เดินเองระหว่างรอบ poll
  const [, forceTick] = useState(0);
  const mounted = useRef(true);
  const [firmware, setFirmware] = useState({ releases: [], has_public_key: true });
  const [fwFile, setFwFile] = useState(null);
  const [fwNote, setFwNote] = useState('');
  const fwInput = useRef(null);

  const loadDevices = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`${API}/admin/monitor-devices`)
      .then(r => setDevices(r.data || []))
      .catch(() => Swal.fire({ icon: 'error', title: 'โหลดรายการจอไม่สำเร็จ' }))
      .finally(() => setLoading(false));
  }, [setLoading]);

  const loadFirmware = useCallback(() => {
    axiosInstance.get(`${API}/admin/monitor-firmware`)
      .then(r => setFirmware(r.data || { releases: [], has_public_key: true }))
      .catch(() => { /* ตารางนี้ยังไม่ได้ migrate ไม่ควรทำให้ทั้งหน้าใช้ไม่ได้ */ });
  }, []);

  // ตัวอย่างไม่ผ่าน setLoading — สปินเนอร์เต็มจอทุก 30 วิใช้งานไม่ได้
  //
  // และการดึงไม่สำเร็จคือ *ข้อมูล* ไม่ใช่ความเงียบ: ถ้าปล่อยให้ค้างเลขเดิมไว้
  // หน้านี้จะเขียวอยู่ทั้งที่เซิร์ฟเวอร์ล่มไปแล้ว ซึ่งเป็นโหมดพังที่แย่ที่สุดของเครื่องมือมอนิเตอร์
  const loadBeacon = useCallback(() => {
    axiosInstance.get(`${API}/admin/monitor-preview`)
      .then(r => {
        if (!mounted.current) return;
        setBeacon(r.data);
        setBeaconFailed(false);
        setFetchedAt(Date.now());
      })
      .catch(() => { if (mounted.current) setBeaconFailed(true); });
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadDevices();
    loadBeacon();
    loadFirmware();
    const poll = setInterval(loadBeacon, POLL_MS);
    const tick = setInterval(() => forceTick(n => n + 1), 10 * 1000);
    return () => { mounted.current = false; clearInterval(poll); clearInterval(tick); };
  }, [loadDevices, loadBeacon, loadFirmware]);

  /* ── ทะเบียนจอ ──────────────────────────────────────────────────────── */

  const saveDevice = () => {
    setFormError(''); setSaving(true);
    const body = {
      device_code: deviceForm.device_code,
      device_name: deviceForm.device_name,
      note: deviceForm.note,
      is_active: deviceForm.is_active,
    };
    const req = deviceForm.device_id
      ? axiosInstance.put(`${API}/admin/monitor-devices/${deviceForm.device_id}`, body)
      : axiosInstance.post(`${API}/admin/monitor-devices`, body);

    req.then(() => { setDeviceForm(null); loadDevices(); })
      .catch(err => setFormError(err?.response?.data?.message || 'บันทึกไม่สำเร็จ'))
      .finally(() => setSaving(false));
  };

  const removeDevice = (device) => {
    Swal.fire({
      icon: 'warning', title: `ลบ "${device.device_name}"?`,
      text: 'จอใบนี้จะดึงตัวเลขไม่ได้อีก จนกว่าจะลงทะเบียนใหม่',
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    }).then(res => {
      if (!res.isConfirmed) return;
      axiosInstance.delete(`${API}/admin/monitor-devices/${device.device_id}`)
        .then(loadDevices)
        .catch(() => Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ' }));
    });
  };

  // token โผล่ครั้งเดียว — ปิดกล่องนี้แล้วอ่านซ้ำไม่ได้ ต้องออกใบใหม่
  const showToken = (device, token) => {
    const config = [
      `#define DEVICE_ID   "${device.device_code}"`,
      `#define MONITOR_KEY "${token}"`,
    ].join('\n');

    Swal.fire({
      icon: 'success',
      title: `token ของ ${device.device_name}`,
      html: `
        <p class="mb-2 text-start">คัดลอกสองบรรทัดนี้ไปวางใน <code>config.h</code> ของจอ แล้วแฟลชใหม่</p>
        <pre class="text-start p-2 rounded" style="background:#f1f3f5;white-space:pre-wrap;word-break:break-all">${config.replace(/</g, '&lt;')}</pre>
        <p class="mb-0 text-start text-danger fw-semibold">ค่านี้แสดงครั้งเดียว ปิดหน้าต่างแล้วดูซ้ำไม่ได้ ถ้าหายต้องออกใหม่</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'คัดลอกทั้งสองบรรทัด',
      cancelButtonText: 'ปิด',
      width: 620,
    }).then(res => { if (res.isConfirmed) copy(config, 'ค่าสำหรับ config.h'); });
  };

  const issueToken = (device) => {
    Swal.fire({
      icon: 'warning',
      title: device.has_token ? 'ออก token ใหม่?' : `ออก token ให้ ${device.device_name}?`,
      text: device.has_token
        ? 'ใบเดิมจะใช้ไม่ได้ทันที จอจะดึงตัวเลขไม่ได้จนกว่าจะแฟลชค่าใหม่ลงไป'
        : 'จอจะยังดึงตัวเลขไม่ได้จนกว่าจะแฟลช token นี้ลงไป',
      showCancelButton: true, confirmButtonText: 'ออก token', cancelButtonText: 'ยกเลิก',
    }).then(res => {
      if (!res.isConfirmed) return;
      axiosInstance.post(`${API}/admin/monitor-devices/${device.device_id}/token`)
        .then(r => { showToken(device, r.data.token); loadDevices(); })
        .catch(err => Swal.fire({
          icon: 'error', title: 'ออก token ไม่สำเร็จ', text: err?.response?.data?.message || '',
        }));
    });
  };

  const clearToken = (device) => {
    Swal.fire({
      icon: 'warning', title: `ถอน token ของ ${device.device_name}?`,
      text: 'ใช้ตอนจอหาย — จอใบนี้จะดึงตัวเลขไม่ได้ทันที โดยไม่กระทบจอใบอื่น',
      showCancelButton: true, confirmButtonText: 'ถอน token', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    }).then(res => {
      if (!res.isConfirmed) return;
      axiosInstance.delete(`${API}/admin/monitor-devices/${device.device_id}/token`)
        .then(loadDevices)
        .catch(() => Swal.fire({ icon: 'error', title: 'ถอน token ไม่สำเร็จ' }));
    });
  };

  /* ── หน้าจอ ─────────────────────────────────────────────────────────── */

  // ── เฟิร์มแวร์ ──
  // คำตอบที่ไม่มี message คือคำตอบที่ไม่ได้มาจาก controller ของเรา — ส่วนใหญ่ nginx ตีกลับ
  // ก่อนถึง node ต้องบอกรหัส HTTP ไว้เสมอ กล่องที่ขึ้นแค่ "ไม่สำเร็จ" เปล่าๆ ไล่สาเหตุไม่ได้
  const uploadErrorText = (e) => {
    const status = e.response?.status;
    if (e.response?.data?.message) return e.response.data.message;
    if (status === 413) {
      return 'ไฟล์ใหญ่เกินที่ nginx ยอมรับ (HTTP 413) — ตั้ง client_max_body_size 4m ให้ /api/admin/monitor-firmware แล้ว reload nginx';
    }
    if (status === 404) return 'เซิร์ฟเวอร์ยังไม่มีเส้นทางนี้ (HTTP 404) — ยังไม่ได้ deploy โค้ดฝั่งเซิร์ฟเวอร์รุ่นที่รองรับเฟิร์มแวร์';
    return status ? `HTTP ${status}` : 'ติดต่อเซิร์ฟเวอร์ไม่ได้';
  };

  const currentFw = firmware.releases.find(r => r.is_current) || null;

  const uploadFirmware = () => {
    if (!fwFile) return;
    const form = new FormData();
    form.append('firmware', fwFile);
    form.append('note', fwNote);
    setLoading(true);
    axiosInstance.post(`${API}/admin/monitor-firmware`, form)
      .then(r => {
        Swal.fire({
          icon: 'success',
          title: `เริ่มแจกรุ่น ${r.data.fw_version}`,
          text: 'จอจะเห็นรุ่นใหม่ในรอบถัดไป (ไม่เกิน 30 วินาที) แล้วอัปเดตเองภายในราวหนึ่งนาที',
        });
        setFwFile(null);
        setFwNote('');
        if (fwInput.current) fwInput.current.value = '';
        loadFirmware();
      })
      .catch(e => {
        // ปิดสปินเนอร์ก่อนเปิดกล่อง ไม่งั้นสปินเนอร์ค้างทับกล่องข้อความ
        setLoading(false);
        Swal.fire({ icon: 'error', title: 'อัปโหลดไม่สำเร็จ', text: uploadErrorText(e) });
      })
      .finally(() => setLoading(false));
  };

  const distribute = (release) => {
    Swal.fire({
      icon: 'question',
      title: `แจกรุ่น ${release.fw_version}?`,
      text: 'จอทุกเครื่องที่ยังไม่ใช่รุ่นนี้จะดาวน์โหลดและรีสตาร์ทเอง รวมถึงกรณีที่รุ่นนี้เก่ากว่ารุ่นที่จอใช้อยู่ (ถอยรุ่น)',
      showCancelButton: true, confirmButtonText: 'แจกรุ่นนี้', cancelButtonText: 'ยกเลิก',
    }).then(({ isConfirmed }) => {
      if (!isConfirmed) return;
      axiosInstance.put(`${API}/admin/monitor-firmware/${release.fw_id}/current`)
        .then(loadFirmware)
        .catch(e => Swal.fire({ icon: 'error', title: e.response?.data?.message || 'ไม่สำเร็จ' }));
    });
  };

  const stopDistribution = () => {
    Swal.fire({
      icon: 'warning',
      title: 'หยุดแจกเฟิร์มแวร์?',
      text: 'จอทุกเครื่องใช้รุ่นที่มีอยู่ต่อไป ไม่ย้อนไปรุ่นไหน',
      showCancelButton: true, confirmButtonText: 'หยุดแจก', cancelButtonText: 'ยกเลิก',
    }).then(({ isConfirmed }) => {
      if (!isConfirmed) return;
      axiosInstance.delete(`${API}/admin/monitor-firmware/current`)
        .then(loadFirmware)
        .catch(e => Swal.fire({ icon: 'error', title: e.response?.data?.message || 'ไม่สำเร็จ' }));
    });
  };

  // รุ่นที่จอรายงานมา เทียบกับรุ่นที่กำลังแจก
  const fwBadge = (d) => {
    if (!d.fw_version) {
      return <div className="fs-sm text-secondary mt-1" title="เฟิร์มแวร์รุ่นก่อนมีการอัปเดตผ่าน WiFi — ต้องเสียบสายแฟลชหนึ่งครั้ง">
        เฟิร์มแวร์ไม่รายงานรุ่น
      </div>;
    }
    const pending = currentFw && currentFw.fw_version !== d.fw_version;
    return <div className="fs-sm mt-1">
      <code>{d.fw_version}</code>
      {pending && <Badge bg="warning" text="dark" className="ms-1">รอลง {currentFw.fw_version}</Badge>}
    </div>;
  };

  const level = beaconFailed ? null : LEVEL[beacon?.level] || null;
  const staleSecs = fetchedAt ? Math.round((Date.now() - fetchedAt) / 1000) : null;

  const stat = (label, value, hint, danger) => (
    <Col xs={6} md={3} className="mb-2 mb-md-0">
      <div className="fs-sm text-secondary">{label}</div>
      <div className={`fs-3 fw-bold lh-1 ${danger ? 'text-danger' : ''}`}>{value}</div>
      {hint && <div className="fs-sm text-secondary">{hint}</div>}
    </Col>
  );

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="main-title mb-1">จอมอนิเตอร์สุขภาพระบบ</h4>
            <p className="text-secondary mb-0 fs-sm">
              จอ ESP32 ที่วางบนโต๊ะผู้ดูแล ดึงตัวเลขของ 60 นาทีล่าสุดทุก 30 วินาที —
              รายละเอียดว่าอะไรพังอยู่ที่หน้า สถิติการใช้งาน
            </p>
          </div>
          <Button size="sm" variant="primary"
            onClick={() => { setFormError(''); setDeviceForm({ ...emptyDevice }); }}>
            <i className="ri-add-line me-1" />เพิ่มจอ
          </Button>
        </div>

        {/* ── ตัวอย่างสิ่งที่จอกำลังแสดง ── */}
        <Card className={`card-one mb-4 border-${beaconFailed ? 'danger' : level?.variant || 'secondary'}`}>
          <Card.Header>
            <Card.Title as="h6" className="mb-0">ตอนนี้จอแสดงอะไรอยู่</Card.Title>
            <span className="fs-sm text-secondary">
              {beaconFailed
                ? 'ดึงข้อมูลไม่ได้'
                : staleSecs === null ? 'กำลังโหลด…' : `อัปเดตเมื่อ ${staleSecs} วินาทีที่แล้ว`}
            </span>
          </Card.Header>
          <Card.Body>
            {beaconFailed ? (
              // ดึงไม่ได้คือสัญญาณที่ดังที่สุด ไม่ใช่ความเงียบ — จอจริงก็ต้องแสดงแบบนี้
              <div className="text-center py-3">
                <div className="fs-1 text-danger lh-1"><i className="ri-wifi-off-line" /></div>
                <div className="fw-bold text-danger mt-2">ติดต่อเซิร์ฟเวอร์ไม่ได้</div>
                <div className="fs-sm text-secondary">
                  จอที่ยังค้างเลขเดิมไว้ตอนนี้กำลังโกหก — ตัวเลขล่าสุดที่เห็นคือของ
                  {fetchedAt ? ` ${new Date(fetchedAt).toLocaleTimeString('th-TH')}` : 'ก่อนหน้านี้'}
                </div>
              </div>
            ) : !beacon ? (
              <div className="text-center text-secondary py-3">กำลังโหลด…</div>
            ) : (
              <>
                <div className="d-flex align-items-center mb-3">
                  <Badge bg={level?.variant} className="fs-6 px-3 py-2">
                    <i className={`${level?.icon} me-1`} />{level?.text}
                  </Badge>
                  <div className="fs-sm text-secondary ms-3">
                    {beacon.reasons?.length
                      ? beacon.reasons.map(r => REASON_TEXT[r] || r).join(' · ')
                      : 'ไม่มีสัญญาณผิดปกติในชั่วโมงที่ผ่านมา'}
                  </div>
                </div>
                <Row className="text-center text-md-start">
                  {stat('5xx (60 นาที)', beacon.err, `จาก ${beacon.req} request`, beacon.err > 0)}
                  {stat('ตอบสนองเฉลี่ย', `${beacon.ms}`, 'มิลลิวินาที')}
                  {stat('บุคลากรที่ใช้งานอยู่', beacon.online, 'ใน 5 นาทีล่าสุด')}
                  {stat('เข้าระบบไม่สำเร็จ', beacon.fail < 0 ? '—' : beacon.fail, '60 นาทีล่าสุด', beacon.fail > 0)}
                </Row>
                <hr className="my-3" />
                <div className="fs-sm text-secondary">
                  ฐานข้อมูลตอบใน {beacon.db < 0
                    ? <span className="text-danger fw-semibold">ต่อไม่ได้</span>
                    : `${beacon.db} ms`} ·
                  เซิร์ฟเวอร์ทำงานมาแล้ว {uptimeText(beacon.up)}
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* ── ทะเบียนจอ ── */}
        <Card className="card-one">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-secondary">
                  <tr>
                    <th>จอ</th>
                    <th style={{ width: 190 }}>สถานะ</th>
                    <th style={{ width: 210 }}>token</th>
                    <th style={{ width: 230 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(d => (
                    <tr key={d.device_id} className={d.is_active ? '' : 'opacity-50'}>
                      <td>
                        <div className="fw-semibold">
                          <i className="ri-device-line me-1" />{d.device_name}
                        </div>
                        <div className="fs-sm text-secondary">
                          <code>{d.device_code}</code>{d.note ? ` · ${d.note}` : ''}
                        </div>
                      </td>
                      <td>
                        {!d.is_active
                          ? <Badge bg="secondary">ปิดใช้งาน</Badge>
                          : <Badge bg={isOnline(d) ? 'success' : 'danger'}>
                              {isOnline(d) ? 'ออนไลน์' : 'ไม่พบสัญญาณ'}
                            </Badge>}
                        <div className="fs-sm text-secondary mt-1">{sinceText(d.last_seen_at)}</div>
                        {d.last_seen_ip && (
                          <div className="fs-sm text-secondary">จาก <code>{d.last_seen_ip}</code></div>
                        )}
                        {fwBadge(d)}
                      </td>
                      <td className="fs-sm">
                        {d.has_token ? (
                          <>
                            <div><code>…{d.token_hint}</code></div>
                            <div className="text-secondary">ออกเมื่อ {thaiDateTime(d.token_set_at)}</div>
                          </>
                        ) : (
                          <span className="text-warning fw-semibold">
                            <i className="ri-error-warning-line me-1" />ยังไม่ได้ออก token
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-primary" className="me-1"
                          onClick={() => issueToken(d)}>
                          <i className="ri-key-2-line me-1" />{d.has_token ? 'ออกใหม่' : 'ออก token'}
                        </Button>
                        {d.has_token && (
                          <Button size="sm" variant="outline-warning" className="me-1"
                            title="ถอน token (ใช้ตอนจอหาย)" onClick={() => clearToken(d)}>
                            <i className="ri-key-line" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline-secondary" className="me-1"
                          onClick={() => {
                            setFormError('');
                            setDeviceForm({
                              device_id: d.device_id, device_code: d.device_code,
                              device_name: d.device_name, note: d.note || '',
                              is_active: !!d.is_active,
                            });
                          }}>
                          <i className="ri-pencil-line" />
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeDevice(d)}>
                          <i className="ri-delete-bin-line" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!devices.length && (
                    <tr>
                      <td colSpan={4} className="text-center text-secondary py-4">
                        ยังไม่มีจอที่ลงทะเบียน
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* ── เฟิร์มแวร์ที่แจกผ่าน WiFi ── */}
        <Card className="card-one mt-4">
          <Card.Header className="d-flex align-items-center">
            <Card.Title as="h6" className="mb-0">เฟิร์มแวร์ที่แจกผ่าน WiFi</Card.Title>
            <span className="ms-auto fs-sm">
              {currentFw
                ? <>กำลังแจก <Badge bg="primary">{currentFw.fw_version}</Badge>
                    <Button size="sm" variant="link" className="text-danger py-0" onClick={stopDistribution}>หยุดแจก</Button></>
                : <span className="text-secondary">ไม่ได้แจกรุ่นไหน</span>}
            </span>
          </Card.Header>
          <Card.Body>
            {!firmware.has_public_key && (
              <div className="alert alert-danger fs-sm">
                เซิร์ฟเวอร์ไม่มีกุญแจสาธารณะ (<code>server/config/monitor-firmware-pubkey.pem</code>) จึงรับไฟล์ไม่ได้
              </div>
            )}
            <p className="fs-sm text-secondary mb-2">
              ไฟล์ต้องลงลายเซ็นก่อนเสมอ — จอตรวจลายเซ็นเองด้วยกุญแจที่ฝังในตัว ไฟล์ที่ไม่ได้ลงลายเซ็นด้วยกุญแจของโรงเรียนจะไม่ถูกลงแม้หลุดผ่านหน้านี้ไปได้
              ขั้นตอน: เพิ่ม <code>FW_VERSION</code> ใน monitor_display.ino → Sketch › Export Compiled Binary →{' '}
              <code>node firmware/tools/sign-firmware.js sign &lt;ไฟล์ .ino.bin&gt;</code> → อัปโหลดไฟล์ <code>.signed.bin</code> ที่นี่
            </p>
            <Row className="g-2 align-items-end mb-3">
              <Col md={5}>
                <Form.Label className="fs-sm mb-1">ไฟล์ที่ลงลายเซ็นแล้ว (.signed.bin)</Form.Label>
                <Form.Control ref={fwInput} type="file" accept=".bin" size="sm"
                  onChange={e => setFwFile(e.target.files?.[0] || null)} />
              </Col>
              <Col md={5}>
                <Form.Label className="fs-sm mb-1">หมายเหตุ (เปลี่ยนอะไร)</Form.Label>
                <Form.Control size="sm" maxLength={255} value={fwNote} onChange={e => setFwNote(e.target.value)} />
              </Col>
              <Col md={2}>
                <Button size="sm" className="w-100" disabled={!fwFile || !firmware.has_public_key} onClick={uploadFirmware}>
                  <i className="ri-upload-2-line me-1" />อัปโหลดและแจก
                </Button>
              </Col>
            </Row>
            <div className="table-responsive">
              <Table size="sm" className="mb-0 align-middle fs-sm">
                <thead className="table-secondary">
                  <tr>
                    <th>รุ่น</th>
                    <th>อัปโหลดเมื่อ</th>
                    <th>ขนาด</th>
                    <th>sha256</th>
                    <th>หมายเหตุ</th>
                    <th style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {firmware.releases.map(r => (
                    <tr key={r.fw_id}>
                      <td><code>{r.fw_version}</code>{r.is_current ? <Badge bg="primary" className="ms-1">แจกอยู่</Badge> : null}</td>
                      <td>{thaiDateTime(r.uploaded_at)}</td>
                      <td>{(r.fw_size / 1024 / 1024).toFixed(2)} MB</td>
                      <td><code title={r.fw_sha256}>{r.fw_sha256.slice(0, 12)}</code></td>
                      <td>{r.note || ''}</td>
                      <td className="text-end">
                        {!r.is_current && (
                          <Button size="sm" variant="outline-primary" onClick={() => distribute(r)}>แจกรุ่นนี้</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!firmware.releases.length && (
                    <tr><td colSpan={6} className="text-center text-secondary py-3">ยังไม่เคยอัปโหลดเฟิร์มแวร์</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* ── ฟอร์มจอ ── */}
        <Modal show={!!deviceForm} onHide={() => setDeviceForm(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{deviceForm?.device_id ? 'แก้ไขจอ' : 'เพิ่มจอ'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <div className="alert alert-danger py-2 fs-sm">{formError}</div>}
            <Form.Group className="mb-3">
              <Form.Label>รหัสจอ</Form.Label>
              <Form.Control
                value={deviceForm?.device_code || ''}
                disabled={!!deviceForm?.device_id}
                placeholder="monitor-1"
                onChange={e => setDeviceForm(f => ({ ...f, device_code: e.target.value }))}
              />
              <Form.Text>ต้องตรงกับ <code>DEVICE_ID</code> ใน config.h ของจอ — ตั้งแล้วเปลี่ยนไม่ได้</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อที่แสดง</Form.Label>
              <Form.Control
                value={deviceForm?.device_name || ''}
                placeholder="จอโต๊ะผู้ดูแลระบบ"
                onChange={e => setDeviceForm(f => ({ ...f, device_name: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                value={deviceForm?.note || ''}
                placeholder="เช่น วางอยู่ที่ไหน"
                onChange={e => setDeviceForm(f => ({ ...f, note: e.target.value }))}
              />
            </Form.Group>
            <Form.Check
              type="switch" label="เปิดใช้งาน" checked={!!deviceForm?.is_active}
              onChange={e => setDeviceForm(f => ({ ...f, is_active: e.target.checked }))}
            />
            {deviceForm?.device_id && !deviceForm?.is_active && (
              <div className="fs-sm text-danger mt-2">
                ปิดแล้วจอใบนี้จะดึงตัวเลขไม่ได้ทันที — ใช้ตอนจอหายโดยไม่ต้องแตะจอใบอื่น
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setDeviceForm(null)}>ยกเลิก</Button>
            <Button variant="primary" disabled={saving} onClick={saveDevice}>บันทึก</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </React.Fragment>
  );
}
